// Copied from Acend
import {
  Box,
  FormErrorMessage,
  FormHelperText,
  HStack,
  Stack,
  StackDirection,
} from "@chakra-ui/react";
import React, { RefObject, useEffect, useRef, useState } from "react";

import FormControl from "./FormControl";
import { FormLabel } from "./FormLabel";
import MultiSelect from "./MultiSelect";
import { MultiSelectProps, Options } from "./selectOptions";

export interface LabeledMultiSelectProps<T extends Options>
  extends MultiSelectProps<T> {
  label?: string | JSX.Element;
  isInputDisabled?: boolean;
  direction?: StackDirection;
  popover?: React.ReactNode;
  errorMessage?: string | string[];
  helperText?: string;
}

/**
 * @example <LabeledMultiSelect
              label="Category"
              name="categories"
              options={CategoryOption}
              setValue={(value) => setState(value)}
              isDisabled={values.caseStatus !== "SCREEN_STATE_UNSPECIFIED"}
              value={values.categories}
              optionsConfig={{
                overrides: { rcaScreenState: "DON'T CLICK ME" },
              }}
              onChange={(categories) => {
                if (categories.length === 0) {
                  setFieldValue("categoryStatuses", []);
                }
              }}
            />
 */

const LabeledMultiSelect = <T extends Options>({
  label,
  name,
  options,
  direction = "column",
  size = "md",
  popover,
  errorMessage,
  helperText,
  isDisabled,
  isInvalid,
  isReadOnly,
  isRequired,
  setValue,
  ...restProps
}: LabeledMultiSelectProps<T>) => {
  const labelRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement | null>(
    null
  );

  const [labelHeight, setlabelHeight] = useState(
    labelRef.current?.clientHeight
  );

  useEffect(() => {
    setlabelHeight(labelRef.current?.clientHeight);
  }, []);

  return (
    <FormControl
      direction={direction}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isReadOnly={isReadOnly}
      isRequired={isRequired}
      data-testid={`form-control-${name}`}
    >
      {label && (
        <HStack
          py={direction === "row" ? 1 : 0}
          w={direction === "row" ? "50%" : "auto"}
          alignItems="baseline"
          justify-content="space-between"
          ref={labelRef}
        >
          <FormLabel isRequired={isRequired} isInvalid={isInvalid}>
            {label}
          </FormLabel>
          <Box as="span">{popover}</Box>
        </HStack>
      )}
      <Box w={direction === "row" ? "50%" : "auto"}>
        <MultiSelect
          {...restProps}
          options={options}
          name={name}
          setValue={setValue}
          labelHeight={direction === "row" ? 0 : labelHeight}
          opacity={isDisabled ? "50%" : "100%"}
          pointerEvents={isDisabled ? "none" : "all"}
          cursor={isDisabled ? "not-allowed" : "inherit"}
          isInvalid={isInvalid}
        />
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column">
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
            {errorMessage && (
              <FormErrorMessage data-testid={`input-error-${name}`}>
                {errorMessage}
              </FormErrorMessage>
            )}
          </Stack>
        </Stack>
      </Box>
    </FormControl>
  );
};

export default LabeledMultiSelect;
